import React, { useState, useEffect } from 'react';
import { formatNumber } from '../utils';

const API_URL = process.env.REACT_APP_API_URL || '';

export default function BackordersPage({ customers, onBackorderChange, refreshTrigger }) {
  const [backorders, setBackorders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [clientFilter, setClientFilter] = useState('ALL');

  // Fetch backorders from API
  useEffect(() => {
    fetchBackorders();
  }, []);

  // Refresh backorders when external changes occur (like check-in fulfilling backorders)
  useEffect(() => {
    if (refreshTrigger) {
      fetchBackorders();
    }
  }, [refreshTrigger]);

  const fetchBackorders = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/backorders`);
      if (!response.ok) {
        throw new Error('Failed to fetch backorders');
      }
      const data = await response.json();
      setBackorders(data);
      setError('');
    } catch (err) {
      console.error('Error fetching backorders:', err);
      setError('Failed to load backorders');
    } finally {
      setLoading(false);
    }
  };

  // Filter backorders by client
  const filteredBackorders = clientFilter === 'ALL' 
    ? backorders 
    : backorders.filter(b => b.stockItem?.clientId === clientFilter);

  // Calculate grand total value
  const grandTotal = filteredBackorders.reduce((sum, b) => {
    return sum + ((b.qty * b.costAtBackorder) / 1000 || 0);
  }, 0);

  // Handle backorder cancellation
  const handleCancelBackorder = async (backorderId) => {
    if (!window.confirm('Are you sure you want to cancel this backorder?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/backorders/${backorderId}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) {
        throw new Error('Failed to cancel backorder');
      }
      
      // Refresh backorders list
      fetchBackorders();
      // Notify parent component of backorder change
      if (onBackorderChange) {
        onBackorderChange();
      }
    } catch (err) {
      console.error('Error cancelling backorder:', err);
      alert('Failed to cancel backorder');
    }
  };

  if (loading) {
    return (
      <div style={{width:'100%',maxWidth:1200,margin:'0 auto',marginTop:24,background:'#fff',borderRadius:24,boxShadow:'0 2px 12px #1976d222',padding:32}}>
        <div style={{textAlign:'center',padding:40,color:'#666'}}>
          Loading backorders...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{width:'100%',maxWidth:1200,margin:'0 auto',marginTop:24,background:'#fff',borderRadius:24,boxShadow:'0 2px 12px #1976d222',padding:32}}>
        <div style={{textAlign:'center',padding:40,color:'#d32f2f'}}>
          {error}
        </div>
      </div>
    );
  }

  return (
    <div style={{width:'100%',maxWidth:1200,margin:'0 auto',marginTop:24,background:'#fff',borderRadius:24,boxShadow:'0 2px 12px #1976d222',padding:32}}>
      <div style={{display:'flex', alignItems:'center', marginBottom:14}}>
        <h3 style={{marginTop:0,marginBottom:0,color:'#1976d2',fontWeight:700,fontSize:'1.25rem',letterSpacing:1,marginRight:18}}>
          Backorders
        </h3>
        <select
          value={clientFilter}
          onChange={e => setClientFilter(e.target.value)}
          style={{
            fontSize:15,
            borderRadius:8,
            border:'1px solid #1976d2',
            padding:'6px 16px',
            fontWeight:600,
            color:'#1976d2',
            background:'#f8fafc',
            marginLeft:4,
            outline:'none',
            boxShadow:'0 1px 6px #1976d211',
            cursor:'pointer'
          }}
        >
          <option value="ALL">All Customers</option>
          {Array.from(new Set(backorders.map(b => b.stockItem?.clientId).filter(Boolean)))
            .map(cid => {
              const cust = customers.find(c => c.id === cid);
              return cust ? <option key={cust.id} value={cust.id}>{cust.name}</option> : null;
            })}
        </select>
      </div>
      
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
        <span style={{fontWeight:600,fontSize:16,color:'#666'}}>
          {filteredBackorders.length} active backorder{filteredBackorders.length !== 1 ? 's' : ''}
        </span>
        <span style={{fontWeight:700,fontSize:18,color:'#1976d2',background:'#e3f2fd',padding:'8px 22px',borderRadius:10,boxShadow:'0 1px 4px #1976d211'}}>
          Grand Total Value: R{formatNumber(grandTotal)}
        </span>
      </div>

      {filteredBackorders.length === 0 ? (
        <div style={{textAlign:'center',padding:40,color:'#666',fontSize:16}}>
          {clientFilter === 'ALL' ? 'No active backorders' : 'No backorders for selected customer'}
        </div>
      ) : (
        <table style={{width:'100%',marginTop:16}}>
          <thead>
            <tr style={{background:'#f8fafc'}}>
              <th style={{padding:'12px 8px',textAlign:'left',fontWeight:700,color:'#1976d2',borderBottom:'2px solid #e0e0e0'}}>Customer</th>
              <th style={{padding:'12px 8px',textAlign:'left',fontWeight:700,color:'#1976d2',borderBottom:'2px solid #e0e0e0'}}>Product Code</th>
              <th style={{padding:'12px 8px',textAlign:'left',fontWeight:700,color:'#1976d2',borderBottom:'2px solid #e0e0e0'}}>Description</th>
              <th style={{padding:'12px 8px',textAlign:'left',fontWeight:700,color:'#1976d2',borderBottom:'2px solid #e0e0e0'}}>Variant</th>
              <th style={{padding:'12px 8px',textAlign:'left',fontWeight:700,color:'#1976d2',borderBottom:'2px solid #e0e0e0'}}>Work Ticket Nr</th>
              <th style={{padding:'12px 8px',textAlign:'right',fontWeight:700,color:'#1976d2',borderBottom:'2px solid #e0e0e0'}}>Qty on Backorder</th>
              <th style={{padding:'12px 8px',textAlign:'right',fontWeight:700,color:'#1976d2',borderBottom:'2px solid #e0e0e0'}}>Cost (R/1000)</th>
              <th style={{padding:'12px 8px',textAlign:'right',fontWeight:700,color:'#1976d2',borderBottom:'2px solid #e0e0e0'}}>Total Value</th>
              <th style={{padding:'12px 8px',textAlign:'left',fontWeight:700,color:'#1976d2',borderBottom:'2px solid #e0e0e0'}}>Status</th>
              <th style={{padding:'12px 8px',textAlign:'left',fontWeight:700,color:'#1976d2',borderBottom:'2px solid #e0e0e0'}}>Created</th>
              <th style={{padding:'12px 8px',textAlign:'center',fontWeight:700,color:'#1976d2',borderBottom:'2px solid #e0e0e0'}}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredBackorders.map((backorder, index) => {
              const totalValue = (backorder.qty * backorder.costAtBackorder) / 1000;
              const customer = customers.find(c => c.id === backorder.stockItem?.clientId);
              const isEven = index % 2 === 0;
              
              return (
                <tr key={backorder.id} style={{background: isEven ? '#fff' : '#fafafa'}}>
                  <td style={{padding:'10px 8px',borderBottom:'1px solid #e0e0e0'}}>
                    {customer?.name || 'Unknown'}
                  </td>
                  <td style={{padding:'10px 8px',borderBottom:'1px solid #e0e0e0',fontWeight:600}}>
                    {backorder.stockItem?.productCode || '-'}
                  </td>
                  <td style={{padding:'10px 8px',borderBottom:'1px solid #e0e0e0'}}>
                    {backorder.stockItem?.mainCategory || '-'}
                  </td>
                  <td style={{padding:'10px 8px',borderBottom:'1px solid #e0e0e0'}}>
                    {backorder.stockItem?.variantDesc || '-'}
                  </td>
                  <td style={{padding:'10px 8px',borderBottom:'1px solid #e0e0e0',fontWeight:600}}>
                    {backorder.orderNumber}
                  </td>
                  <td style={{padding:'10px 8px',borderBottom:'1px solid #e0e0e0',textAlign:'right',fontWeight:600,color:'#d32f2f'}}>
                    {formatNumber(backorder.qty)}
                  </td>
                  <td style={{padding:'10px 8px',borderBottom:'1px solid #e0e0e0',textAlign:'right'}}>
                    R{formatNumber(backorder.costAtBackorder)}
                  </td>
                  <td style={{padding:'10px 8px',borderBottom:'1px solid #e0e0e0',textAlign:'right',fontWeight:600}}>
                    R{formatNumber(totalValue)}
                  </td>
                  <td style={{padding:'10px 8px',borderBottom:'1px solid #e0e0e0'}}>
                    <span style={{
                      padding:'4px 8px',
                      borderRadius:12,
                      fontSize:12,
                      fontWeight:600,
                      background: backorder.status === 'pending' ? '#fff3e0' : '#e8f5e8',
                      color: backorder.status === 'pending' ? '#f57c00' : '#2e7d32'
                    }}>
                      {backorder.status === 'pending' ? 'Pending' : 'Partially Fulfilled'}
                    </span>
                  </td>
                  <td style={{padding:'10px 8px',borderBottom:'1px solid #e0e0e0',fontSize:14,color:'#666'}}>
                    {new Date(backorder.createdAt).toLocaleDateString()}
                  </td>
                  <td style={{padding:'10px 8px',borderBottom:'1px solid #e0e0e0',textAlign:'center'}}>
                    <button
                      onClick={() => handleCancelBackorder(backorder.id)}
                      style={{
                        background:'#d32f2f',
                        color:'#fff',
                        border:'none',
                        borderRadius:6,
                        padding:'6px 12px',
                        fontSize:12,
                        fontWeight:600,
                        cursor:'pointer',
                        boxShadow:'0 1px 3px rgba(0,0,0,0.2)'
                      }}
                      title="Cancel backorder"
                    >
                      Cancel
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      
      {backorders.length > 0 && (
        <div style={{marginTop:20,padding:16,background:'#f8fafc',borderRadius:8,fontSize:14,color:'#666'}}>
          <strong>Note:</strong> Backorders are automatically fulfilled when stock is checked in (oldest first). 
          Once fulfilled, backorders are removed from this list but retained for reporting purposes.
        </div>
      )}
    </div>
  );
}
